import { useFocusEffect } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import useRewardsHomeDataV2 from 'features/rewards/RewardsHome/hooks/useRewardsHomeDataV2';
import { REWARDS_HOME } from 'features/rewards/RewardsHome/RewardsHome.constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import ExpressiveSmText from 'shared/components/vxl/typography/expressive/ExpressiveSmText';
import { isWeb } from 'shared/constants/platform';
import SectionedContentScreen from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import TabDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/TabDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import theme from 'shared/styles/theme';
import { useGetLocationCoordinates } from 'shared/utils/location/getLocationCoordinates';
import {
  showZendeskArticle,
  ZendeskArticles,
} from 'shared/utils/zendesk/zendeskUtils';

const CashbackHomeScreen = () => {
  const insets = useSafeAreaInsets();
  const hasFocusedOnce = useRef(false);
  const { access, hasLocationFetched, getLocationCoordinates } =
    useGetLocationCoordinates();

  // Initialize shouldPause based on whether location has already been fetched
  const [shouldPause, setShouldPause] = useState(!hasLocationFetched.current);

  const { isFeatureEnabled } = useStatsig();
  const isDynamicMapEntryPointEnabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_DYNAMIC_MAP_ENTRY_POINT,
  );

  const {
    loading,
    response,
    errors,
    loadMore,
    reload,
    refreshAfterActivation,
    fetchRewardsData,
  } = useRewardsHomeDataV2(shouldPause);

  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'rewardsHome',
  });

  useEffect(() => {
    const handleLocationAccess = async () => {
      if (access === 'checking' || hasLocationFetched.current) {
        return;
      }

      hasLocationFetched.current = true;
      const { coordinates } = await getLocationCoordinates({
        shouldRequestPermission: isDynamicMapEntryPointEnabled,
      });

      setShouldPause(false);

      // coordinates value would be either from GPS or zip code lookup
      if (coordinates) {
        fetchRewardsData(coordinates);
      }
    };

    void handleLocationAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access]);

  useFocusEffect(
    useCallback(() => {
      if (!hasFocusedOnce.current) {
        hasFocusedOnce.current = true;
        return;
      }
      refreshAfterActivation();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
  );

  const isLoading =
    shouldPause || access === 'checking' || loading || !response;
  return (
    <>
      <StatusBar style="dark" />
      <View style={styles.container}>
        <ScreenTransitionOverlay
          isTabPage
          isLoading={isLoading}
          LoadingComponent={
            <View style={[styles.overlay, { paddingTop: insets.top }]}>
              <LoadingSpinner />
            </View>
          }
          ErrorComponent={
            errors ? (
              <TabDownErrorMessage
                error={errors ?? ['Cashback home error occurred']}
                illustrationKey="HOURGLASS_A"
                tryAgainAction={reload}
              />
            ) : null
          }
        />
        <View style={styles.headerContainer}>
          <NavigationBar
            title=""
            empty
            color={theme.color.background.tertiary}
            trailingAccessories={
              <View style={styles.buttonWrapper}>
                <IconButton
                  iconKey="INFO"
                  iconButtonAccessibilityLabel="INFO"
                  iconOnPress={() =>
                    showZendeskArticle(ZendeskArticles.CASHBACK)
                  }
                  analyticsId="cashbackFaqClicked"
                />
              </View>
            }
          />
        </View>
        <View style={styles.contentContainer}>
          {!isLoading && response?.sectionedContentModel ? (
            <SectionedContentScreen
              initialNumToRender={REWARDS_HOME.initialNumToRender}
              model={{
                header: {
                  id: 'cashback-home',
                  render: () => (
                    <View style={styles.titleContainer}>
                      <ExpressiveSmText style={styles.titleText}>
                        {translatedText('title')}
                      </ExpressiveSmText>
                    </View>
                  ),
                },
                ...(response?.sectionedContentModel ?? { sections: [] }),
              }}
              onEndReached={loadMore}
              onEndReachedThreshold={REWARDS_HOME.reachedThreshold}
            />
          ) : null}
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  buttonWrapper: { height: 20 },
  container: {
    backgroundColor: theme.color.background.tertiary,
    flex: 1,
    flexDirection: 'column',
  },
  contentContainer: {
    flex: 1,
  },
  headerContainer: {
    paddingTop: isWeb ? 12 : 0,
  },
  overlay: {
    ...StyleSheet.absoluteFill,
    justifyContent: 'center',
    alignItems: 'center',
  },
  titleContainer: {
    marginHorizontal: theme.spacing.m,
    marginBottom: theme.spacing.m,
  },
  titleText: {
    textTransform: 'uppercase',
  },
});

export default CashbackHomeScreen;
